import { useRef, useState } from "react";
import { Camera, Loader2, CheckCircle2, X, ImageIcon, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { submitProductPhoto } from "../lib/api";

const MAX_DIM = 1920;            // resize longest side
const QUALITY = 0.85;            // JPEG quality after resize
const COMPRESS_THRESHOLD = 2 * 1024 * 1024;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function approxBytesFromDataUrl(dataUrl) {
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  return Math.ceil((b64.length * 3) / 4);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function compressDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const scale = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", QUALITY));
    };
    img.src = dataUrl;
  });
}

/**
 * Single image picker slot — shows preview, change, and clear buttons.
 * Compresses on selection so the upload payload is always <2 MB.
 */
function PhotoSlot({ kind, label, hint, value, onChange, busy }) {
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type)) {
      toast.error("That file type isn't supported", {
        description: "Please choose a JPG, PNG, or WebP image — those work best.",
      });
      return;
    }
    try {
      let dataUrl = await fileToDataUrl(file);
      if (approxBytesFromDataUrl(dataUrl) > COMPRESS_THRESHOLD) {
        dataUrl = await compressDataUrl(dataUrl);
      }
      if (approxBytesFromDataUrl(dataUrl) > MAX_UPLOAD_BYTES) {
        toast.error("That image is still too large", {
          description: "Try a tighter crop or a slightly smaller resolution.",
        });
        return;
      }
      onChange(dataUrl);
    } catch (err) {
      toast.error("Couldn't read that image", {
        description: "Try a different file — JPG / PNG / WebP work best.",
      });
    }
  };

  if (value) {
    return (
      <div
        data-testid={`photo-slot-${kind}-filled`}
        className="rounded-2xl border border-stone-200 bg-stone-50 p-2.5 space-y-2"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wider font-bold text-stone-700">
            {label}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => onChange(null)}
            data-testid={`photo-slot-${kind}-clear`}
            className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-900"
          >
            <X className="h-3 w-3" /> Remove
          </button>
        </div>
        <div className="relative w-full overflow-hidden rounded-xl bg-white border border-stone-200">
          <img
            src={value}
            alt={`${label} preview`}
            data-testid={`photo-slot-${kind}-preview`}
            className="w-full max-h-56 object-contain"
          />
        </div>
      </div>
    );
  }

  return (
    <label
      data-testid={`photo-slot-${kind}-empty`}
      className="block rounded-2xl border border-dashed border-stone-300 bg-white p-4 cursor-pointer hover:border-stone-500 hover:bg-stone-50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-stone-100 border border-stone-200 shrink-0">
          {kind === "front"
            ? <ImageIcon className="w-4 h-4 text-stone-700" />
            : <Layers className="w-4 h-4 text-stone-700" />}
        </span>
        <div className="space-y-0.5 min-w-0">
          <div className="text-[13px] font-semibold text-stone-900">{label}</div>
          <div className="text-[11.5px] text-stone-500 leading-snug">{hint}</div>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        capture="environment"
        onChange={onPick}
        disabled={busy}
        data-testid={`photo-slot-${kind}-input`}
        className="sr-only"
      />
    </label>
  );
}

export default function PhotoSubmissionCTA({ productId }) {
  const [open, setOpen] = useState(false);
  const [front, setFront] = useState(null);
  const [ingredients, setIngredients] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!front || busy) return;
    setBusy(true);
    try {
      await submitProductPhoto(productId, {
        front_image: front,
        ingredients_image: ingredients || undefined,
        reporter_email: email.trim() || undefined,
      });
      setDone(true);
      toast.success("Thanks \u2014 we'll review your photo", {
        description: "A reviewer typically gets to new submissions within 48 hours.",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "We couldn't save that photo just now.");
      toast.error("Photo couldn't be saved", {
        description: typeof msg === "string" ? msg : "Check your connection and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section
        data-testid="photo-submission-thanks"
        className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 sm:p-6 flex items-start gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h2 className="text-[15px] font-bold text-emerald-900">
            Submitted for review
          </h2>
          <p className="text-[13px] text-emerald-800/90 leading-relaxed">
            A reviewer will verify the photo. Once approved, this product page
            will show real imagery for everyone.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="photo-submission-cta"
      className="rounded-2xl bg-cream-100/70 border border-surface-line p-5 sm:p-6 space-y-3.5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-surface-line shrink-0">
          <Camera className="w-4.5 h-4.5 text-ink-soft" />
        </span>
        <div className="space-y-1">
          <h2 className="text-[15px] font-bold text-ink">
            Help improve this product
          </h2>
          <p className="text-[13px] text-ink-soft leading-relaxed max-w-prose">
            Snap the front of the package — and the ingredients label too if
            you have it on hand. Approved photos help everyone trust this page.
          </p>
        </div>
      </div>

      {!open && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="photo-submission-open"
          variant="outline"
          className="border-surface-line text-ink hover:bg-white"
          size="sm"
        >
          <Camera className="w-4 h-4 mr-1.5" />
          Add product photo
        </Button>
      )}

      {open && (
        <div className="space-y-3">
          <PhotoSlot
            kind="front"
            label="Front label (required)"
            hint="A clear shot of the package front — full bottle/tube visible."
            value={front}
            onChange={setFront}
            busy={busy}
          />
          <PhotoSlot
            kind="ingredients"
            label="Ingredients label (optional)"
            hint="If you can read the INCI list clearly, we'll use it to enrich the data."
            value={ingredients}
            onChange={setIngredients}
            busy={busy}
          />

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-1.5">
              Email (optional)
            </span>
            <input
              data-testid="photo-submission-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-surface-line bg-white px-3.5 h-11 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-200 focus:border-ink-mute"
            />
            <span className="block mt-1 text-[12px] text-ink-mute">
              Only used if our reviewer needs to clarify something.
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              type="button"
              onClick={submit}
              disabled={!front || busy}
              data-testid="photo-submission-submit"
              className="bg-ink hover:bg-ink-soft text-cream-50"
              size="sm"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Submit for review
            </Button>
            <button
              type="button"
              onClick={() => { setOpen(false); setFront(null); setIngredients(null); }}
              data-testid="photo-submission-cancel"
              disabled={busy}
              className="text-[13px] font-semibold text-ink-mute hover:text-ink disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
